import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "@/lib/http.ts";
import { isAxiosError } from "axios";
import type {
  ContactCommunicationThreadsPage,
  ContactEventsResponse,
} from "@/types/contactActivity.ts";
import {
  type BioBuildPhase,
  PROFILE_BUILD_POLL_MS,
  PROFILE_BUILD_WAIT_MS,
} from "@/shared/contactProfile.ts";
import type { ContactProfileResponse } from "@/types/contactProfile.ts";
import type { MemoryResp } from "@/types/memory.ts";

const COMMUNICATIONS_PAGE_SIZE = 20;

/**
 * Query factories for the contact sidebar's activity tabs (BIG-5337): the
 * communications and calendar events a contact participates in.
 */
export const contactActivityQueries = {
  communications: (contactId: string) =>
    infiniteQueryOptions({
      queryKey: ["contactActivity", contactId, "communications"] as const,
      queryFn: async ({ pageParam, signal }) => {
        const params = new URLSearchParams({ limit: String(COMMUNICATIONS_PAGE_SIZE) });
        if (pageParam) params.set("cursor", pageParam);
        const response = await api.get<ContactCommunicationThreadsPage>(
          `/api/a/contacts/${contactId}/communications?${params}`,
          { signal },
        );
        return response.data;
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }),
  events: (contactId: string) =>
    queryOptions({
      queryKey: ["contactActivity", contactId, "events"] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<ContactEventsResponse>(
          `/api/a/contacts/${contactId}/events`,
          {
            signal,
          },
        );
        return response.data;
      },
    }),
  memories: (contactId: string) =>
    queryOptions({
      queryKey: ["contactActivity", contactId, "memories"] as const,
      queryFn: async ({ signal }): Promise<MemoryResp[]> => {
        const response = await api.get<MemoryResp[]>(`/api/a/contacts/${contactId}/memories`, {
          signal,
        });
        return response.data;
      },
    }),
  profile: (contactId: string) =>
    queryOptions({
      queryKey: ["contactActivity", contactId, "profile"] as const,
      queryFn: async ({ signal }): Promise<ContactProfileResponse | null> => {
        try {
          const response = await api.get<ContactProfileResponse>(
            `/api/a/contacts/${contactId}/profile`,
            { signal },
          );
          return response.data;
        } catch (err) {
          // A contact with no profile built yet is an expected empty state, not an error.
          if (isAxiosError(err) && err.response?.status === 404) return null;
          throw err;
        }
      },
    }),
};

export const useContactCommunications = (contactId: string) =>
  useInfiniteQuery(contactActivityQueries.communications(contactId));

export const useContactEvents = (contactId: string) =>
  useQuery(contactActivityQueries.events(contactId));

export const useContactProfile = (contactId: string, enabled = true) =>
  useQuery({ ...contactActivityQueries.profile(contactId), enabled });

/**
 * A Bio build for a contact with no profile: where it stands and how to start one.
 * Only `enabled` for a contact whose profile query resolved to null.
 *
 * The server keeps no build state, so this tracks only a build started here. While it
 * runs, a second observer polls the profile query, so whoever reads that query sees
 * the bio the moment it lands. After `PROFILE_BUILD_WAIT_MS` without one the panel
 * offers the button again, and keeps polling in case the build is just slow.
 */
export const useBioBuild = (contactId: string, enabled: boolean) => {
  const queryClient = useQueryClient();
  const profile = contactActivityQueries.profile(contactId);
  const [phase, setPhase] = useState<BioBuildPhase>("idle");
  const building = enabled && phase === "building";
  // Polling outlasts the wait: past it the panel offers a retry, but the build job
  // can run for half an hour, so a bio that lands late should still show up.
  useQuery({
    ...profile,
    enabled: enabled && phase !== "idle",
    refetchInterval: PROFILE_BUILD_POLL_MS,
  });

  useEffect(() => {
    if (!building) return;
    const timer = setTimeout(() => setPhase("slow"), PROFILE_BUILD_WAIT_MS);
    return () => clearTimeout(timer);
  }, [building]);

  const start = useMutation({
    // A 200 carries a bio the contact already had, so there is nothing to build.
    mutationFn: async (): Promise<ContactProfileResponse | null> => {
      const response = await api.post<ContactProfileResponse>(
        `/api/a/contacts/${contactId}/profile/build`,
      );
      return response.status === 200 ? response.data : null;
    },
    onSuccess: (existing) => {
      if (existing) queryClient.setQueryData(profile.queryKey, existing);
      else setPhase("building");
    },
  });

  return { phase, start };
};

export const useContactMemories = (contactId: string) =>
  useQuery(contactActivityQueries.memories(contactId));
